/**
 * useTenantBranding — React Query hook for the current tenant's branding.
 *
 * This is a thin wrapper around the existing `useBrandingQuery` hook
 * from `use-branding.ts`. Its purpose is to:
 *
 * 1. Provide a dedicated import path for the white-label theming feature
 *    so `TenantBrandingProvider` does not couple itself to the broader
 *    branding editor's API surface.
 *
 * 2. Add the sysadmin-skip gate: when the access token's `aud` claim is
 *    `'argusai-admin'` the `/branding` endpoint 401s because sysadmin
 *    context has no tenant. We detect this synchronously from the
 *    in-memory JWT (no extra network call) and disable the query,
 *    allowing `TenantBrandingProvider` to fall back to platform defaults.
 *
 * 3. Expose `isReady` — true when either (a) custom branding was fetched
 *    successfully, or (b) the query is disabled / failed and the caller
 *    should use defaults. Consumers use `isReady` to avoid a brief
 *    "wrong theme" flash before the first fetch resolves.
 *
 * ## Usage
 *
 * ```tsx
 * const { branding, isReady, hasCustomBranding } = useTenantBranding();
 * ```
 */

import { useBrandingQuery } from './use-branding';
import type { EffectiveBranding } from './use-branding';
import type { TenantBranding } from './branding-defaults';

// ─── Sysadmin detection ──────────────────────────────────────────────────────

/**
 * Returns `true` when the current access token is a sysadmin token
 * (audience `'argusai-admin'`). Reads from `localStorage` synchronously —
 * no async work, no additional network call.
 *
 * Treats malformed / missing tokens as tenant context (returns `false`).
 */
export function isSysadminToken(): boolean {
  const token = typeof localStorage !== 'undefined'
    ? localStorage.getItem('accessToken')
    : null;
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    return payload?.aud === 'argusai-admin';
  } catch {
    return false;
  }
}

// ─── useTenantBranding ───────────────────────────────────────────────────────

export interface UseTenantBrandingResult {
  /**
   * The resolved `TenantBranding` from the API, or `null` when the
   * query is disabled, still loading, or returned an error.
   * `null` means "use platform defaults".
   */
  branding: TenantBranding | null;

  /**
   * `true` when the hook has a definitive answer — either a loaded
   * branding object or a confirmed "use defaults" state. Components
   * should wait for `isReady` before rendering theme-sensitive UI to
   * avoid a one-frame flash of the wrong theme.
   */
  isReady: boolean;

  /**
   * `true` when the API returned custom (non-default) branding that
   * was successfully applied. Useful for conditional rendering of
   * "custom theme active" indicators in the admin UI.
   */
  hasCustomBranding: boolean;

  /** Pass-through from the underlying React Query result. */
  isLoading: boolean;

  /** Pass-through from the underlying React Query result. */
  error: unknown;
}

/**
 * Fetches the current tenant's effective branding from `GET /branding`
 * via React Query and normalises the result for `TenantBrandingProvider`.
 *
 * The query is **disabled** when:
 * - There is no access token in localStorage (unauthenticated).
 * - The access token is a sysadmin token (no tenant context).
 *
 * In both disabled cases, `branding` is `null` and `isReady` is `true`
 * immediately (no loading state).
 */
export function useTenantBranding(): UseTenantBrandingResult {
  const skipFetch = isSysadminToken() || (
    typeof localStorage !== 'undefined' && !localStorage.getItem('accessToken')
  );

  const { data, isLoading, isSuccess, isError, error } = useBrandingQuery({
    enabled: !skipFetch,
  });

  // Cast EffectiveBranding → TenantBranding: the shapes are compatible
  // (TenantBranding is the superset used by the branding-defaults module,
  // EffectiveBranding is the wire shape from use-branding.ts — both carry
  // light/dark/logoUrl/etc.). We cast here rather than duplicating types.
  const branding = data as unknown as TenantBranding | null | undefined;

  // "Ready" when: query is disabled, or query has settled (success or error).
  const isReady = skipFetch || isSuccess || isError;

  // Custom branding is "active" when the API returned data whose source
  // is NOT 'system_default' AND useDefaultTheme is false.
  const hasCustomBranding = Boolean(
    branding &&
      !branding.useDefaultTheme &&
      (branding as EffectiveBranding).source !== 'system_default',
  );

  return {
    branding: branding ?? null,
    isReady,
    hasCustomBranding,
    isLoading: !skipFetch && isLoading,
    error,
  };
}
